// ---------------------------------------------------------------------------
// C5: Integration Hub Dashboard
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonCard } from "@/components/skeleton";
import { CheckCircle2, CircleAlert, Loader2, Link2, Unplug } from "lucide-react";

interface Connection {
  id: string;
  providerId: string;
  displayName: string | null;
  status: string;
  syncEnabled: boolean;
  lastUsedAt: string | null;
  lastErrorMessage: string | null;
  failureCount: number;
}

interface MetaPage {
  id: string;
  name: string;
  category: string;
}

interface MetaReview {
  id: string;
  rating: number;
  text: string;
  authorName: string;
  createdAt: string;
}

const PROVIDERS: Record<string, { name: string; icon: string; category: string }> = {
  ghl: { name: "GoHighLevel", icon: "G", category: "CRM" },
  hubspot: { name: "HubSpot", icon: "H", category: "CRM" },
  meta: { name: "Meta", icon: "M", category: "Social" },
  salesforce: { name: "Salesforce", icon: "S", category: "CRM" },
  zoho: { name: "Zoho CRM", icon: "Z", category: "CRM" },
  shopify: { name: "Shopify", icon: "Shop", category: "E-Commerce" },
};

function statusMeta(status: string) {
  if (status === "active") {
    return {
      label: "Connected",
      tone: "text-[var(--color-success)] bg-[var(--color-success)]/10",
      Icon: CheckCircle2,
    };
  }

  if (status === "error") {
    return {
      label: "Error",
      tone: "text-[var(--color-destructive)] bg-[var(--color-destructive)]/10",
      Icon: CircleAlert,
    };
  }

  if (status === "expired") {
    return {
      label: "Expired",
      tone: "text-[var(--color-warning)] bg-[var(--color-warning)]/10",
      Icon: CircleAlert,
    };
  }

  if (status === "revoked") {
    return {
      label: "Reconnect required",
      tone: "text-[var(--color-destructive)] bg-[var(--color-destructive)]/10",
      Icon: CircleAlert,
    };
  }

  return {
    label: "Not connected",
    tone: "text-muted-foreground bg-muted",
    Icon: Link2,
  };
}

function shouldShowReconnect(status: string) {
  return status === "error" || status === "expired" || status === "revoked";
}

export default function IntegrationDashboard() {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectingProviderId, setConnectingProviderId] = useState<string | null>(null);
  const [disconnectingConnectionId, setDisconnectingConnectionId] = useState<string | null>(null);
  const [metaPages, setMetaPages] = useState<MetaPage[]>([]);
  const [isLoadingMetaPages, setIsLoadingMetaPages] = useState(false);
  const [loadingReviewsPageId, setLoadingReviewsPageId] = useState<string | null>(null);
  const [metaReviewsByPageId, setMetaReviewsByPageId] = useState<Record<string, MetaReview[]>>({});

  const fetchConnections = useCallback(async () => {
    try {
      const data = await apiFetch<{ data: Connection[] }>("/api/integrations/connections");
      setConnections(data.data ?? []);
      setError(null);
    } catch (err) {
      setConnections([]);
      setError(err instanceof Error ? err.message : "Integration status is currently unavailable.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const fetchMetaPages = useCallback(async () => {
    setIsLoadingMetaPages(true);
    try {
      const data = await apiFetch<{ data: { pages: MetaPage[] } }>("/api/integrations/meta/pages");
      setMetaPages(data.data.pages ?? []);
    } catch (err) {
      setMetaPages([]);
      setError(err instanceof Error ? err.message : "Connected Meta pages are currently unavailable.");
    } finally {
      setIsLoadingMetaPages(false);
    }
  }, []);

  useEffect(() => {
    const hasMeta = connections.some((connection) => connection.providerId === "meta" && connection.status === "active");
    if (!hasMeta) {
      setMetaPages([]);
      setMetaReviewsByPageId({});
      return;
    }
    void fetchMetaPages();
  }, [connections, fetchMetaPages]);

  const handleConnect = async (providerId: string) => {
    setConnectingProviderId(providerId);
    try {
      const { authUrl } = await apiFetch<{ authUrl: string }>("/api/integrations/oauth/initiate", {
        method: "POST",
        body: JSON.stringify({ providerId }),
      });
      window.location.href = authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to initiate the integration connection.");
    } finally {
      setConnectingProviderId(null);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    if (!confirm("Disconnect this integration? Sync will stop.")) return;
    setDisconnectingConnectionId(connectionId);
    try {
      await apiFetch(`/api/integrations/connections/${connectionId}`, { method: "DELETE" });
      await fetchConnections();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect the integration.");
    } finally {
      setDisconnectingConnectionId(null);
    }
  };

  const handleLoadMetaReviews = async (pageId: string) => {
    setLoadingReviewsPageId(pageId);
    try {
      const data = await apiFetch<{ data: { reviews: MetaReview[] } }>(`/api/integrations/meta/pages/${pageId}/reviews`);
      setMetaReviewsByPageId((current) => ({
        ...current,
        [pageId]: data.data.reviews ?? [],
      }));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Meta reviews.");
    } finally {
      setLoadingReviewsPageId(null);
    }
  };

  if (isLoading) {
    return <div className="space-y-4">{Array.from({ length: 3 }, (_, i) => <SkeletonCard key={i} className="h-20" />)}</div>;
  }

  const connectedIds = new Set(connections.map(c => c.providerId));
  const availableProviders = Object.entries(PROVIDERS).filter(([id]) => !connectedIds.has(id));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Integrations</h1>
        <button onClick={() => setShowConnect(!showConnect)} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
          {showConnect ? "Close" : "Connect New"}
        </button>
      </div>

      {error && (
        <EmptyState
          title="Integration status unavailable"
          description={error}
        />
      )}

      {showConnect && (
        <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-background p-4 sm:grid-cols-2 lg:grid-cols-3">
          {availableProviders.map(([id, p]) => (
            <button key={id} onClick={() => void handleConnect(id)} disabled={connectingProviderId === id} className="flex items-center gap-3 rounded-md border border-border p-3 text-left hover:bg-accent disabled:opacity-50">
              <span className="inline-flex h-10 min-w-10 items-center justify-center rounded-full bg-muted px-2 text-sm font-semibold">{p.icon}</span>
              <div>
                <div className="font-medium text-foreground">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.category}</div>
              </div>
              {connectingProviderId === id ? <Loader2 className="ml-auto h-4 w-4 animate-spin text-muted-foreground" /> : null}
            </button>
          ))}
        </div>
      )}

      {connections.length === 0 && !showConnect ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <p className="text-lg text-muted-foreground">No integrations connected yet</p>
          <button onClick={() => setShowConnect(true)} className="mt-3 text-sm font-medium text-primary">Connect your first integration</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {connections.map((conn) => {
            const provider = PROVIDERS[conn.providerId] ?? { name: conn.providerId, icon: "🔗", category: "Other" };
            const status = statusMeta(conn.status);
            return (
              <div key={conn.id} className="rounded-lg border border-border bg-background p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-muted px-2 text-sm font-semibold">{provider.icon}</span>
                    <span className="font-medium text-foreground">{conn.displayName ?? provider.name}</span>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${status.tone}`}>
                    <status.Icon className="h-3.5 w-3.5" />
                    {status.label}
                  </span>
                </div>
                {conn.lastErrorMessage && (
                  <p className="mt-2 text-xs text-[var(--color-destructive)]">{conn.lastErrorMessage}</p>
                )}
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{conn.syncEnabled ? "Sync on" : "Sync off"}</span>
                  <span>{conn.lastUsedAt ? `Used ${new Date(conn.lastUsedAt).toLocaleDateString()}` : "Never used"}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  {shouldShowReconnect(conn.status) ? (
                    <button
                      type="button"
                      onClick={() => void handleConnect(conn.providerId)}
                      disabled={connectingProviderId === conn.providerId}
                      className="rounded border border-[var(--color-warning)]/30 px-3 py-1 text-xs text-[var(--color-warning)] hover:bg-[var(--color-warning)]/5 disabled:opacity-50"
                    >
                      {connectingProviderId === conn.providerId ? "Reconnecting..." : `Reconnect ${provider.name}`}
                    </button>
                  ) : null}
                  <a href={`/settings/integrations/${conn.id}/sync`} className="rounded border border-border px-3 py-1 text-xs hover:bg-accent">Configure</a>
                  <button onClick={() => void handleDisconnect(conn.id)} disabled={disconnectingConnectionId === conn.id} className="inline-flex items-center gap-1 rounded border border-[var(--color-destructive)]/20 px-3 py-1 text-xs text-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/5 disabled:opacity-50">
                    {disconnectingConnectionId === conn.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />}
                    Disconnect
                  </button>
                </div>
                {conn.providerId === "meta" ? (
                  <div className="mt-4 rounded-md border border-border/70 bg-muted/30 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">Connected pages</p>
                        <p className="text-xs text-muted-foreground">Loaded from the mounted Graph API page discovery route.</p>
                      </div>
                      {isLoadingMetaPages ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
                    </div>
                    {metaPages.length === 0 && !isLoadingMetaPages ? (
                      <p className="mt-3 text-xs text-muted-foreground">No Pages are visible on this Meta connection yet.</p>
                    ) : null}
                    <div className="mt-3 space-y-3">
                      {metaPages.map((page) => {
                        const reviews = metaReviewsByPageId[page.id] ?? [];
                        return (
                          <div key={page.id} className="rounded-md border border-border bg-background p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-medium text-foreground">{page.name}</p>
                                <p className="text-xs text-muted-foreground">{page.category}</p>
                              </div>
                              <button
                                onClick={() => void handleLoadMetaReviews(page.id)}
                                disabled={loadingReviewsPageId === page.id}
                                className="rounded border border-border px-2.5 py-1 text-xs hover:bg-accent disabled:opacity-50"
                              >
                                {loadingReviewsPageId === page.id ? "Loading..." : "Load reviews"}
                              </button>
                            </div>
                            {reviews.length > 0 ? (
                              <div className="mt-3 space-y-2">
                                {reviews.map((review) => (
                                  <div key={review.id} className="rounded border border-border/70 bg-muted/20 p-2">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-xs font-medium text-foreground">{review.authorName}</span>
                                      <span className="text-xs text-muted-foreground">{review.rating}/5</span>
                                    </div>
                                    <p className="mt-1 text-xs text-muted-foreground">{review.text || "No written review text."}</p>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
